import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CategoryManagementPanel = () => {
  const [categories, setCategories] = useState([
    { id: 1, name: 'Wedding Photography', type: 'service', parent: null, order: 1, active: true },
    { id: 2, name: 'Mehndi Artists', type: 'service', parent: null, order: 2, active: true },
    { id: 3, name: 'Wedding Venues', type: 'service', parent: null, order: 3, active: true },
    { id: 4, name: 'Catering Services', type: 'service', parent: null, order: 4, active: true },
    { id: 5, name: 'Bridal Wear', type: 'service', parent: null, order: 5, active: true },
    { id: 6, name: 'Wedding Planners', type: 'service', parent: null, order: 6, active: true },
    { id: 7, name: 'Decorators', type: 'service', parent: null, order: 7, active: true },
    { id: 8, name: 'DJ Services', type: 'service', parent: null, order: 8, active: true }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const [newCategory, setNewCategory] = useState({
    name: '',
    type: 'service',
    parent: null,
    description: '',
    active: true
  });

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || category.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleAddCategory = () => {
    if (newCategory.name.trim()) {
      const newId = Math.max(...categories.map(c => c.id)) + 1;
      setCategories([...categories, {
        ...newCategory,
        id: newId,
        order: categories.length + 1
      }]);
      setNewCategory({
        name: '',
        type: 'service',
        parent: null,
        description: '',
        active: true
      });
      setShowAddForm(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      type: category.type,
      parent: category.parent,
      description: category.description || '',
      active: category.active
    });
  };

  const handleUpdateCategory = () => {
    if (editingCategory && newCategory.name.trim()) {
      setCategories(categories.map(cat =>
        cat.id === editingCategory.id
          ? { ...cat, ...newCategory }
          : cat
      ));
      setEditingCategory(null);
      setNewCategory({
        name: '',
        type: 'service',
        parent: null,
        description: '',
        active: true
      });
    }
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setCategories(categories.filter(cat => cat.id !== id));
    }
  };

  const handleToggleActive = (id) => {
    setCategories(categories.map(cat =>
      cat.id === id ? { ...cat, active: !cat.active } : cat
    ));
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setShowAddForm(false);
    setNewCategory({
      name: '',
      type: 'service',
      parent: null,
      description: '',
      active: true
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Category Management
        </h2>
        <p className="text-white/90">
          Manage vendor and service categories
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
          <h3 className="font-heading font-semibold text-lg text-text-primary">
            Categories ({filteredCategories.length})
          </h3>
          <Button
            variant="primary"
            onClick={() => setShowAddForm(!showAddForm)}
            iconName="Plus"
            iconSize={16}
          >
            Add Category
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="Search"
            />
          </div>
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="service">Service</option>
              <option value="product">Product</option>
              <option value="venue">Venue</option>
            </select>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingCategory) && (
        <div className="bg-white rounded-xl shadow-soft p-6">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Category Name
              </label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                placeholder="Enter category name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Type
              </label>
              <select
                value={newCategory.type}
                onChange={(e) => setNewCategory({...newCategory, type: e.target.value})}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="service">Service</option>
                <option value="product">Product</option>
                <option value="venue">Venue</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Description
              </label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                placeholder="Enter category description"
                rows={3}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active"
                checked={newCategory.active}
                onChange={(e) => setNewCategory({...newCategory, active: e.target.checked})}
                className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
              />
              <label htmlFor="active" className="text-sm text-text-primary">
                Active
              </label>
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-6">
            <Button variant="ghost" onClick={cancelEdit}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={editingCategory ? handleUpdateCategory : handleAddCategory}
            >
              {editingCategory ? 'Update' : 'Add'} Category
            </Button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border">
              {filteredCategories.map((category) => (
                <tr key={category.id} className="hover:bg-surface-100">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                        <Icon name="Folder" size={16} className="text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-text-primary">
                          {category.name}
                        </div>
                        {category.parent && (
                          <div className="text-xs text-text-secondary">
                            Parent: {category.parent}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      category.type === 'service' ? 'bg-blue-100 text-blue-800' :
                      category.type === 'product'? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {category.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      category.active ? 'bg-success-100 text-success' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {category.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {category.order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="text-primary hover:text-primary-600 transition-colors"
                      >
                        <Icon name="Edit" size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(category.id)}
                        className={`${category.active ? 'text-gray-500' : 'text-success'} hover:opacity-75 transition-opacity`}
                      >
                        <Icon name={category.active ? 'EyeOff' : 'Eye'} size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementPanel;